import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpIcon, MicIcon, PlusIcon, SparklesIcon } from 'lucide-react';
import { heroPrompts } from '../data/homepage';
import { AtnaConversationDialog } from './demandes/AtnaConversationDialog';
import { InscriptionDialog } from './loisirs/InscriptionDialog';
import { detectCategory } from '../hooks/useInscriptionConversation';
const PANEL_LAYOUT_ID = 'atna-hero-panel';
type Flow = 'signalement' | 'inscription';

/** Picks the most likely ATNA flow from what the resident typed. */
function detectFlow(text: string): Flow {
  const lower = text.toLowerCase();
  const wantsRegistration = /regist|sign\s?up|class|lesson|activit|camp|session|swim|skat|program|course/.test(lower) || detectCategory(lower) !== null;
  return wantsRegistration ? 'inscription' : 'signalement';
}
export function HeroAtna() {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [conversation, setConversation] = useState<{
    flow: Flow;
    text: string;
  } | null>(null);
  const openConversation = (text: string, flow?: Flow) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setConversation({
      flow: flow ?? detectFlow(trimmed),
      text: trimmed
    });
    setValue('');
  };
  return <section aria-labelledby="hero-atna-title" className="w-full rounded-3xl border border-white/60 bg-white p-5 shadow-2xl shadow-ville-navy/20 sm:p-6" data-id="element-1291" data-mp="opr4d8es1pxp">
      <h2 id="hero-atna-title" className="sr-only" data-id="element-1292" data-mp="1he4yhd2qw23z">
        ATNA assistant
      </h2>

      {conversation ?
    // Holds the height while the bar morphs into the dialog.
    <div className="h-[54px]" aria-hidden="true" data-id="element-1293" data-mp="18cx6tduqhziv" /> : <motion.div layoutId={PANEL_LAYOUT_ID} style={{
      borderRadius: 999
    }} transition={{
      duration: 0.28,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-1294" data-mp="18b3xfqsql176">
          <form onSubmit={event => {
        event.preventDefault();
        openConversation(value);
      }} className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1.5 pl-3 pr-1.5 transition-colors duration-150 ease-ville focus-within:border-ville-indigo" data-id="element-1295" data-mp="3t3xlxgpocaxx">
            <button type="button" onClick={() => openConversation(value)} aria-label="Attach a photo or a document" className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-ville-mist hover:text-ville-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1296" data-mp="1ourswlm16j0s">
              <PlusIcon className="h-5 w-5" aria-hidden="true" data-id="element-1297" data-mp="11x6p7ss9978l" />
            </button>
            <label htmlFor="hero-atna-input" className="sr-only" data-id="element-1298" data-mp="30caz8muwr23k">
              Ask ATNA
            </label>
            <input id="hero-atna-input" type="text" value={value} onChange={event => setValue(event.target.value)} placeholder="Ask ATNA…" className="min-w-0 flex-1 border-0 bg-transparent py-2 text-base text-ville-navy placeholder:text-slate-400 focus:outline-none" data-id="element-1299" data-mp="2wazasknrsmwy" />
            <button type="button" aria-label="Dictate your question" className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-ville-mist hover:text-ville-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1300" data-mp="ruun092h83i9">
              <MicIcon className="h-5 w-5" aria-hidden="true" data-id="element-1301" data-mp="83ppvqqo48kt" />
            </button>
            <button type="submit" aria-label="Send your question to ATNA" className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ville-lime text-ville-navy transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-1302" data-mp="d43uszwz32em">
              <ArrowUpIcon className="h-5 w-5" aria-hidden="true" data-id="element-1303" data-mp="1i3k0ezlvmcsh" />
            </button>
          </form>
        </motion.div>}

      <ul className="mt-3 flex flex-wrap gap-2" data-id="element-1304" data-mp="1w433dsbhisns">
        {heroPrompts.map(prompt => <li key={prompt.label} data-id="element-1305" data-mp="1s057dqhghxyy">
            <button type="button" onClick={() => prompt.label === 'Register my child' ? openConversation(prompt.label, 'inscription') : navigate(prompt.to)} className="rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1306" data-mp="1s3avp3i9gvtm">
              {prompt.label}
            </button>
          </li>)}
      </ul>

      <p className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-ville-indigo" data-id="element-1307" data-mp="33f7cu2a0gxyn">
        <SparklesIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1308" data-mp="38bo4h6gf5ugh" />
        ATNA knows the Town of Bonnyville’s services
      </p>

      <AnimatePresence data-id="element-1309" data-mp="2ufajvogl9cxs">
        {conversation?.flow === 'signalement' && <AtnaConversationDialog key="atna-hero-dialog" initialText={conversation.text} layoutId={PANEL_LAYOUT_ID} onClose={() => setConversation(null)} data-id="element-1310" data-mp="2yd89uti6o328" />}
        {conversation?.flow === 'inscription' && <InscriptionDialog key="atna-hero-inscription" initialText={conversation.text} layoutId={PANEL_LAYOUT_ID} onClose={() => setConversation(null)} data-id="element-1311" data-mp="2aq9xo813jha4" />}
      </AnimatePresence>
    </section>;
}